import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Building2, User, Edit, Link as LinkIcon, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const ProjectsPageEnhanced = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [migrationResults, setMigrationResults] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [migrating, setMigrating] = useState(false);
  
  const [formData, setFormData] = useState({
    company_id: '',
    contact_id: '',
  });

  const [newProjectData, setNewProjectData] = useState({
    project_name: '',
    revenue: '',
    hst: '',
    status: 'active',
    company_id: '',
    contact_id: '',
  });

  const [editProjectData, setEditProjectData] = useState({
    project_name: '',
    revenue: '',
    hst: '',
    status: 'active',
    company_id: '',
    contact_id: '',
  });

  useEffect(() => {
    fetchProjects();
    fetchCompanies();
    fetchContacts();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleLinkCompany = (project) => {
    setCurrentProject(project);
    setFormData({
      company_id: project.company_id || '',
      contact_id: project.contact_id || '',
    });
    setDialogOpen(true);
  };

  const handleSubmitLink = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/projects/${currentProject.id}`, formData);
      toast.success('Project linked successfully');
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error linking project:', error);
      toast.error('Failed to link project');
    }
  };

  const handleAddProject = () => {
    setNewProjectData({
      project_name: '',
      revenue: '',
      hst: '',
      status: 'active',
      company_id: '',
      contact_id: '',
    });
    setAddDialogOpen(true);
  };

  const handleEditProject = (project) => {
    setCurrentProject(project);
    setEditProjectData({
      project_name: project.project_name || '',
      revenue: project.revenue || '',
      hst: project.hst || '',
      status: project.status || 'active',
      company_id: project.company_id || '',
      contact_id: project.contact_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleSubmitNewProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...newProjectData,
        revenue: parseFloat(newProjectData.revenue) || 0,
        hst: parseFloat(newProjectData.hst) || 0,
      };
      await api.post('/projects', projectData);
      toast.success('Project created successfully');
      setAddDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleSubmitEditProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...editProjectData,
        revenue: parseFloat(editProjectData.revenue) || 0,
        hst: parseFloat(editProjectData.hst) || 0,
      };
      await api.put(`/projects/${currentProject.id}`, projectData);
      toast.success('Project updated successfully');
      setEditDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/projects/${projectToDelete.id}`);
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleRunMigration = async () => {
    setMigrating(true);
    try {
      const response = await api.post('/admin/migrate-project-links');
      setMigrationResults(response.data);
      setMigrationDialogOpen(true);
      fetchProjects();
      toast.success(`Auto-linked ${response.data.auto_linked} projects`);
    } catch (error) {
      console.error('Error running migration:', error);
      toast.error('Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const handleValidateData = async () => {
    try {
      const response = await api.post('/admin/validate-data-integrity');
      if (response.data.issues_found === 0) {
        toast.success('All data is valid!');
      } else {
        toast.warning(`Found ${response.data.issues_found} data integrity issues`);
        console.log('Data integrity issues:', response.data.issues);
      }
    } catch (error) {
      console.error('Error validating data:', error);
      toast.error('Validation failed');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : '-';
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? `${contact.first_name} ${contact.last_name}` : '-';
  };

  const linkedProjects = projects.filter(p => p.company_id || p.contact_id);
  const unlinkedProjects = projects.filter(p => !p.company_id && !p.contact_id);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Projects</h1>
          <p className="text-slate-600 mt-1">Manage project relationships with companies and contacts</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleAddProject} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Project
          </Button>
          <Button variant="outline" onClick={handleValidateData}>
            Validate Data
          </Button>
          <Button 
            onClick={handleRunMigration} 
            disabled={migrating}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${migrating ? 'animate-spin' : ''}`} />
            {migrating ? 'Migrating...' : 'Auto-Link Projects'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{projects.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Linked Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{linkedProjects.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Need Linking</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{unlinkedProjects.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Projects</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project Name</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>HST (%)</TableHead>
                <TableHead>Total (incl. HST)</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No projects found
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell className="font-medium">
                      <button
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="text-[#247C75] hover:text-[#1d6259] hover:underline font-medium transition-colors"
                      >
                        {project.project_name}
                      </button>
                    </TableCell>
                    <TableCell>${(project.revenue || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      {project.hst ? `${project.hst}%` : '0%'}
                      {project.hst && project.revenue ? (
                        <span className="text-xs text-slate-500 ml-2">
                          (${((project.revenue * project.hst) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-semibold">
                      ${((project.revenue || 0) + ((project.revenue || 0) * ((project.hst || 0) / 100))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {project.company_id ? (
                          <>
                            <Building2 className="h-4 w-4 text-blue-600" />
                            <span className="text-sm">{getCompanyName(project.company_id)}</span>
                          </>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-200">
                            Not Linked
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {project.contact_id ? (
                          <>
                            <User className="h-4 w-4 text-purple-600" />
                            <span className="text-sm">{getContactName(project.contact_id)}</span>
                          </>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={project.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {project.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditProject(project)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLinkCompany(project)}
                        >
                          <LinkIcon className="h-4 w-4 mr-1" />
                          Link
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(project)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Link Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Project to Company/Contact</DialogTitle>
            <DialogDescription>
              Link "{currentProject?.project_name}" to a company and/or contact
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitLink} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_id">Company</Label>
              <Select
                value={formData.company_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, company_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_id">Contact</Label>
              <Select
                value={formData.contact_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Link Project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Project Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Project</DialogTitle>
            <DialogDescription>
              Create a new project and optionally link it to a company/contact
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitNewProject} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project_name">Project Name *</Label>
              <Input
                id="project_name"
                value={newProjectData.project_name}
                onChange={(e) => setNewProjectData({ ...newProjectData, project_name: e.target.value })}
                placeholder="Enter project name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="revenue">Revenue</Label>
              <Input
                id="revenue"
                type="number"
                step="0.01"
                value={newProjectData.revenue}
                onChange={(e) => setNewProjectData({ ...newProjectData, revenue: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hst">HST (%)</Label>
              <Input
                id="hst"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={newProjectData.hst}
                onChange={(e) => setNewProjectData({ ...newProjectData, hst: e.target.value })}
                placeholder="13.00"
              />
              <p className="text-xs text-slate-500">Enter HST as percentage (e.g., 13 for 13%)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newProjectData.status}
                onValueChange={(value) => setNewProjectData({ ...newProjectData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_id">Company (Optional)</Label>
              <Select
                value={newProjectData.company_id || undefined}
                onValueChange={(value) => setNewProjectData({ ...newProjectData, company_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_id">Contact (Optional)</Label>
              <Select
                value={newProjectData.contact_id || undefined}
                onValueChange={(value) => setNewProjectData({ ...newProjectData, contact_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Create Project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>
              Update project details for "{currentProject?.project_name}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEditProject} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_project_name">Project Name *</Label>
              <Input
                id="edit_project_name"
                value={editProjectData.project_name}
                onChange={(e) => setEditProjectData({ ...editProjectData, project_name: e.target.value })}
                placeholder="Enter project name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_revenue">Revenue</Label>
              <Input
                id="edit_revenue"
                type="number"
                step="0.01"
                value={editProjectData.revenue}
                onChange={(e) => setEditProjectData({ ...editProjectData, revenue: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_hst">HST (%)</Label>
              <Input
                id="edit_hst"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={editProjectData.hst}
                onChange={(e) => setEditProjectData({ ...editProjectData, hst: e.target.value })}
                placeholder="13.00"
              />
              <p className="text-xs text-slate-500">Enter HST as percentage (e.g., 13 for 13%)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editProjectData.status}
                onValueChange={(value) => setEditProjectData({ ...editProjectData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_company_id">Company (Optional)</Label>
              <Select
                value={editProjectData.company_id || undefined}
                onValueChange={(value) => setEditProjectData({ ...editProjectData, company_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_contact_id">Contact (Optional)</Label>
              <Select
                value={editProjectData.contact_id || undefined}
                onValueChange={(value) => setEditProjectData({ ...editProjectData, contact_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update Project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectToDelete?.project_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migration Results Dialog */}
      <AlertDialog open={migrationDialogOpen} onOpenChange={setMigrationDialogOpen}>
        <AlertDialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Migration Results</AlertDialogTitle>
            <AlertDialogDescription>
              Auto-linked {migrationResults?.auto_linked} projects. Review suggestions below:
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {migrationResults && migrationResults.suggestions.length > 0 && (
            <div className="space-y-2 mt-4">
              <h4 className="font-medium text-sm">Suggested Matches (for manual review):</h4>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {migrationResults.suggestions.map((suggestion, idx) => (
                  <div key={idx} className="p-3 border rounded-lg bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{suggestion.project_name}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          → Suggested: {suggestion.suggested_company_name}
                        </p>
                      </div>
                      <Badge className={
                        suggestion.confidence > 70 ? 'bg-green-100 text-green-800' : 
                        suggestion.confidence > 40 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-orange-100 text-orange-800'
                      }>
                        {suggestion.confidence.toFixed(0)}% match
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setMigrationDialogOpen(false)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPageEnhanced;
